import React, { useState, useEffect } from "react";
import { Modal, Select, Input, Button, Space, Typography, Alert, Spin, Tag } from "antd";
import { FrontDeskBooking } from "../../../types";
import { frontDeskService } from "../../../services/adminService";

const { Text } = Typography;
const { TextArea } = Input;

interface ChangeRoomModalProps {
  open: boolean;
  booking: FrontDeskBooking | null;
  onCancel: () => void;
  onChangeRoom: (bookingId: string, roomId: string, reason?: string) => Promise<void>;
  rooms: any[];
  roomTypes: any[];
}

export const ChangeRoomModal: React.FC<ChangeRoomModalProps> = ({
  open,
  booking,
  onCancel,
  onChangeRoom,
  rooms,
  roomTypes,
}) => {
  const [selectedRoomId, setSelectedRoomId] = useState<string | undefined>(undefined);
  const [reason, setReason] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [availableRooms, setAvailableRooms] = useState<any[]>([]);

  useEffect(() => {
    if (open && booking) {
      setSelectedRoomId(undefined);
      setReason("");
      setError(null);

      const currentRoomType = booking.room_type_id;
      const filtered = rooms.filter(
        (r: any) =>
          r.room_type_id === currentRoomType &&
          r.id !== booking.room_id &&
          r.status === "available",
      );
      setAvailableRooms(filtered);
    }
  }, [open, booking, rooms]);

  const handleChangeRoom = async () => {
    if (!booking || !selectedRoomId) return;

    setLoading(true);
    setError(null);
    try {
      await onChangeRoom(booking.id, selectedRoomId, reason || undefined);
      onCancel();
    } catch (err: any) {
      setError(err?.response?.data?.message || err?.message || "Failed to change room");
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    setSelectedRoomId(undefined);
    setReason("");
    setError(null);
    onCancel();
  };

  return (
    <Modal
      title="Change Room"
      open={open}
      onCancel={handleCancel}
      footer={null}
      width={460}
    >
      {booking && (
        <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
          <div style={{ background: "#f5f5f5", padding: 12, borderRadius: 8 }}>
            <div><Text strong>Guest:</Text> {booking.guest_name}</div>
            <div>
              <Text strong>Current Room:</Text>{" "}
              {booking.room_number || "Not allocated"}
            </div>
            <div>
              <Text strong>Room Type:</Text>{" "}
              {roomTypes.find((rt: any) => rt.id === booking.room_type_id)?.name || "—"}
            </div>
          </div>

          <div>
            <Text strong>New Room</Text>
            <Select
              value={selectedRoomId}
              onChange={setSelectedRoomId}
              style={{ width: "100%", marginTop: 8 }}
              placeholder="Select a room"
              showSearch
              optionFilterProp="label"
            >
              {availableRooms.map((room: any) => (
                <Select.Option key={room.id} value={room.id} label={`Room ${room.room_number}`}>
                  Room {room.room_number}
                  {room.floor ? ` (Floor ${room.floor})` : ""}
                </Select.Option>
              ))}
            </Select>
            {availableRooms.length === 0 && (
              <Text type="secondary" style={{ fontSize: 12 }}>
                No available rooms of the same type
              </Text>
            )}
          </div>

          <div>
            <Text strong>Reason (optional)</Text>
            <TextArea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              rows={2}
              placeholder="e.g. Guest request, maintenance issue..."
              style={{ marginTop: 8 }}
            />
          </div>

          {error && (
            <Alert type="error" showIcon message={error} />
          )}

          <Space style={{ justifyContent: "flex-end" }}>
            <Button onClick={handleCancel}>Cancel</Button>
            <Button
              type="primary"
              onClick={handleChangeRoom}
              disabled={!selectedRoomId || loading}
              loading={loading}
            >
              Change Room
            </Button>
          </Space>
        </div>
      )}
    </Modal>
  );
};
